import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/index.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LangProvider } from "@/contexts/LangContext";
import { CartProvider } from "@/contexts/CartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Categories from "@/pages/Categories";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Account from "@/pages/Account";
import Favorites from "@/pages/Favorites";
import Login from "@/pages/Login";

// USM (Store Manager) — separate internal sub-app
import { UsmAuthProvider } from "@/contexts/UsmAuthContext";
import { UsmPrefsProvider } from "@/contexts/UsmPrefsContext";
import UsmLogin from "@/pages/usm/UsmLogin";
import UsmLayout from "@/pages/usm/UsmLayout";
import ScannerMobilePage from "@/pages/scan/ScannerMobilePage";
import { ScannerSessionProvider } from "@/contexts/ScannerSessionContext";
import UsmDashboard from "@/pages/usm/Dashboard";
import UsmAccounting from "@/pages/usm/Accounting";
import UsmBranches from "@/pages/usm/branches/BranchesList";
import UsmEmployees from "@/pages/usm/employees/EmployeesList";
import UsmInventory from "@/pages/usm/inventory/InventoryList";
import UsmInventoryImport from "@/pages/usm/inventory/InventoryImport";
import UsmInventoryTimeline from "@/pages/usm/inventory/Timeline";
import UsmAudit from "@/pages/usm/audit/AuditList";
import UsmBackup from "@/pages/usm/BackupPage";
import UsmPriceLookup from "@/pages/usm/PriceLookup";
import UsmLabelTemplates from "@/pages/usm/LabelTemplates";
import UsmNotes from "@/pages/usm/NotesPage";
import UsmPos from "@/pages/usm/Pos";
import UsmSalesList from "@/pages/usm/sales/SalesList";
import UsmSaleDetail from "@/pages/usm/sales/SaleDetail";
import UsmReturn from "@/pages/usm/sales/Return";
import UsmTransfersList from "@/pages/usm/transfers/TransfersList";
import UsmTransferDetail from "@/pages/usm/transfers/TransferDetail";
import UsmCashClose from "@/pages/usm/cash/CashClose";
import UsmCashCloseList from "@/pages/usm/cash/CashCloseList";
import UsmCashCloseDetail from "@/pages/usm/cash/CashCloseDetail";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Rates from "@/pages/Rates";
import TVMode from "@/pages/TVMode";
import Admin from "@/pages/Admin";
import WhatsAppButton from "@/components/WhatsAppButton";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Skip auto-scroll for /products listing — that page restores its own scroll position
    // for proper back-button UX (see Products.jsx).
    if (pathname === "/products") return;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SeoLoader() {
  useEffect(() => {
    let aborted = false;
    fetch("/api/settings/seo").then((r) => r.json()).then((seo) => {
      if (aborted || !seo) return;
      // Update meta tags
      const setMeta = (sel, attr, val) => {
        if (!val) return;
        let el = document.querySelector(sel);
        if (!el) {
          el = document.createElement("meta");
          if (sel.includes("name=")) el.setAttribute("name", sel.split("name=\"")[1].split("\"")[0]);
          if (sel.includes("property=")) el.setAttribute("property", sel.split("property=\"")[1].split("\"")[0]);
          document.head.appendChild(el);
        }
        el.setAttribute(attr, val);
      };
      const lang = (localStorage.getItem("uk_lang") || "tr");
      const title = lang === "tr" ? seo.homepage_title_tr : seo.homepage_title_en;
      const desc = lang === "tr" ? seo.homepage_description_tr : seo.homepage_description_en;
      if (title) document.title = title;
      setMeta('meta[name="description"]', "content", desc);
      setMeta('meta[name="keywords"]', "content", seo.homepage_keywords);
      setMeta('meta[property="og:title"]', "content", seo.og_title);
      setMeta('meta[property="og:description"]', "content", seo.og_description);
      setMeta('meta[property="og:image"]', "content", seo.og_image);
      setMeta('meta[name="robots"]', "content", seo.robots);
      // Google Search Console
      if (seo.google_site_verification && !document.querySelector('meta[name="google-site-verification"]')) {
        const m = document.createElement("meta");
        m.setAttribute("name", "google-site-verification");
        m.setAttribute("content", seo.google_site_verification);
        document.head.appendChild(m);
      }
      // GA4
      if (seo.google_analytics_id && !document.querySelector(`script[data-ga="${seo.google_analytics_id}"]`)) {
        const s1 = document.createElement("script");
        s1.async = true;
        s1.dataset.ga = seo.google_analytics_id;
        s1.src = `https://www.googletagmanager.com/gtag/js?id=${seo.google_analytics_id}`;
        document.head.appendChild(s1);
        const s2 = document.createElement("script");
        s2.dataset.ga = seo.google_analytics_id;
        s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${seo.google_analytics_id}');`;
        document.head.appendChild(s2);
      }
      // GTM
      if (seo.google_tag_manager_id && !document.querySelector(`script[data-gtm="${seo.google_tag_manager_id}"]`)) {
        const s = document.createElement("script");
        s.dataset.gtm = seo.google_tag_manager_id;
        s.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${seo.google_tag_manager_id}');`;
        document.head.appendChild(s);
      }
    }).catch(() => {});
    return () => { aborted = true; };
  }, []);
  return null;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><span className="text-sm text-[#52525B]">Loading...</span></div>;  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !["super_admin", "admin", "content_manager", "order_manager"].includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  const loc = useLocation();
  // TV mode and Admin use their own layouts
  if (loc.pathname.startsWith("/tv")) {
    return (
      <Routes>
        <Route path="/tv" element={<TVMode />} />
      </Routes>
    );
  }
  // Phase 54.26 — Mobile Scanner is a public page (no auth). Rendered
  // outside UsmLayout / UsmAuthProvider so it can be reached from a
  // phone that has never logged into the ERP.
  if (loc.pathname.startsWith("/scan/")) {
    return (
      <Routes>
        <Route path="/scan/:session_id" element={<ScannerMobilePage />} />
      </Routes>
    );
  }
  if (loc.pathname.startsWith("/admin")) {
    return (
      <Routes>
        <Route path="/admin/*" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      </Routes>
    );
  }
  if (loc.pathname.startsWith("/usm")) {
    return (
      <ScannerSessionProvider>
        <Routes>
          <Route path="/usm/login" element={<UsmLogin />} />
          <Route path="/usm" element={<UsmLayout />}>
          <Route index element={<UsmDashboard />} />
          <Route path="price-lookup" element={<UsmPriceLookup />} />
          <Route path="branches" element={<UsmBranches />} />
          <Route path="employees" element={<UsmEmployees />} />
          <Route path="inventory" element={<Navigate to="/usm/inventory/silver" replace />} />
          <Route path="inventory/silver" element={<UsmInventory scope="silver" />} />
          <Route path="inventory/gold" element={<UsmInventory scope="gold" />} />
          <Route path="inventory/watch" element={<UsmInventory scope="watch" />} />
          <Route path="inventory/import" element={<UsmInventoryImport />} />
          <Route path="inventory/:iid/timeline" element={<UsmInventoryTimeline />} />
          <Route path="accounting" element={<UsmAccounting />} />
          <Route path="audit" element={<UsmAudit />} />
          <Route path="system/backup" element={<UsmBackup />} />
          <Route path="notes" element={<UsmNotes />} />
          <Route path="labels" element={<UsmLabelTemplates />} />
          <Route path="pos" element={<UsmPos />} />
          <Route path="sales" element={<UsmSalesList />} />
          <Route path="sales/return" element={<UsmReturn />} />
          <Route path="sales/:sid" element={<UsmSaleDetail />} />
          <Route path="transfers" element={<UsmTransfersList />} />
          <Route path="transfers/:tid" element={<UsmTransferDetail />} />
          <Route path="cash-close" element={<Navigate to="/usm/accounting?tab=cash-close" replace />} />
          <Route path="cash-close/history" element={<Navigate to="/usm/accounting?tab=cash-close" replace />} />
          <Route path="cash-close/:cid" element={<UsmCashCloseDetail />} />
        </Route>
        </Routes>
      </ScannerSessionProvider>
    );
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/rates" element={<ProtectedRoute adminOnly><Rates /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

// Renders customer-facing widgets (WhatsApp FAB, etc.). Hidden on internal
// routes (/usm, /admin, /tv) so the internal sub-apps stay clean.
function StorefrontChrome() {
  const loc = useLocation();
  if (loc.pathname.startsWith("/usm") || loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/tv")) return null;
  return <WhatsAppButton />;
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <UsmAuthProvider>
          <UsmPrefsProvider>
          <FavoritesProvider>
            <CartProvider>
              <BrowserRouter>
                <ScrollToTop />
                <SeoLoader />
                <AppRoutes />
                <StorefrontChrome />
                <Toaster position="top-right" />
              </BrowserRouter>
            </CartProvider>
          </FavoritesProvider>
          </UsmPrefsProvider>
        </UsmAuthProvider>
      </AuthProvider>
    </LangProvider>
  );
}
